/**
 * BatchManager.js
 * 
 * Service for organizing files into implementation batches.
 * Handles dependency analysis and optimal batch creation.
 */

class BatchManager {
  constructor(storageManager) {
    this.storage = storageManager;
    this.MAX_BATCH_SIZE = 300; // Max LOC per batch
    this.MAX_BATCH_FILES = 3;  // Max files per batch
  }
  
  /**
   * Create initial implementation batches based on components
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Created batches
   */
  async createInitialBatches(projectId) {
    // Get project components and files
    const components = await this.storage.getComponentsByProject(projectId);
    const files = await this.storage.getFilesByProject(projectId);
    
    // Build dependency graph
    const graph = this.buildDependencyGraph(components);
    
    // Sort components by dependency order
    const sortedComponents = this.topologicalSort(graph);
    
    // Group files into batches
    const batches = [];
    let currentBatch = [];
    let currentSize = 0;
    let batchNumber = 1;
    
    for (const componentId of sortedComponents) {
      const component = components.find(c => c.id === componentId);
      if (!component) continue;
      
      // Get files for this component
      const componentFiles = files.filter(f => f.componentId === componentId);
      
      for (const file of componentFiles) {
        // Check if adding this file would exceed batch limits
        if ((currentSize + file.estimatedLoc > this.MAX_BATCH_SIZE || 
             currentBatch.length >= this.MAX_BATCH_FILES) && 
            currentBatch.length > 0) {
          // Create a new batch
          batches.push({
            id: `batch_${batchNumber}`,
            projectId,
            name: `Batch ${batchNumber}`,
            files: [...currentBatch],
            status: 'pending',
            totalLoc: currentSize,
            priority: batchNumber
          });
          
          // Reset for next batch
          currentBatch = [];
          currentSize = 0;
          batchNumber++;
        }
        
        // Add file to current batch
        currentBatch.push(file.id);
        currentSize += file.estimatedLoc;
      }
    }
    
    // Add final batch if not empty
    if (currentBatch.length > 0) {
      batches.push({
        id: `batch_${batchNumber}`,
        projectId,
        name: `Batch ${batchNumber}`,
        files: [...currentBatch],
        status: 'pending',
        totalLoc: currentSize,
        priority: batchNumber
      });
    }
    
    // Save batches
    for (const batch of batches) {
      await this.storage.saveBatch(batch);
    }
    
    return batches;
  }
  
  /**
   * Build component dependency graph
   * @param {Array} components - Project components
   * @returns {Object} Dependency graph
   */
  buildDependencyGraph(components) {
    const graph = {};
    
    // Initialize graph with empty dependencies
    for (const component of components) {
      graph[component.id] = [];
    }
    
    // Add dependencies
    for (const component of components) {
      if (component.dependencies) {
        for (const dep of component.dependencies) {
          if (graph[dep]) {
            graph[dep].push(component.id);
          }
        }
      }
    }
    
    return graph;
  }
  
  /**
   * Sort components in topological order
   * @param {Object} graph - Dependency graph
   * @returns {Array} Sorted component IDs
   */
  topologicalSort(graph) {
    // Implementation of topological sort for directed graph
    const visited = {};
    const temp = {};
    const order = [];
    
    const visit = (node) => {
      // If node is permanently visited, do nothing
      if (visited[node]) return;
      
      // If node is temporarily visited, we have a cycle
      if (temp[node]) {
        throw new Error('Dependency cycle detected');
      }
      
      // Mark node as temporarily visited
      temp[node] = true;
      
      // Visit all dependencies
      for (const dep of graph[node]) {
        visit(dep);
      }
      
      // Mark node as permanently visited
      temp[node] = false;
      visited[node] = true;
      
      // Add node to order
      order.unshift(node);
    };
    
    // Visit all nodes
    for (const node in graph) {
      if (!visited[node]) {
        visit(node);
      }
    }
    
    return order;
  }
  
  /**
   * Get the next pending batch
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Next batch
   */
  async getNextBatch(projectId) {
    // Get all batches for project
    const batches = await this.storage.getBatchesByProject(projectId);
    
    // Find first pending batch
    return batches
      .filter(b => b.status === 'pending')
      .sort((a, b) => a.priority - b.priority)[0];
  }
  
  /**
   * Update batch status
   * @param {string} batchId - Batch ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated batch
   */
  async updateBatchStatus(batchId, status) {
    const batch = await this.storage.getBatch(batchId);
    if (!batch) throw new Error(`Batch ${batchId} not found`);
    
    batch.status = status;
    await this.storage.saveBatch(batch);
    return batch;
  }
  
  /**
   * Optimize batches based on file dependencies
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Optimized batches
   */
  async optimizeBatches(projectId) {
    // Get all components, files, and existing batches
    const components = await this.storage.getComponentsByProject(projectId);
    const files = await this.storage.getFilesByProject(projectId);
    const existingBatches = await this.storage.getBatchesByProject(projectId);
    
    // Only optimize pending batches
    const pendingBatches = existingBatches.filter(b => b.status === 'pending');
    if (pendingBatches.length === 0) return [];
    
    // Get file dependencies
    const fileDependencies = await this.analyzeFileDependencies(files);
    
    // Create new optimized batches
    const optimizedBatches = this.createOptimizedBatches(files, fileDependencies);
    
    // Update existing pending batches
    for (let i = 0; i < pendingBatches.length && i < optimizedBatches.length; i++) {
      pendingBatches[i].files = optimizedBatches[i];
      pendingBatches[i].totalLoc = this.calculateBatchSize(optimizedBatches[i], files);
      await this.storage.saveBatch(pendingBatches[i]);
    }
    
    // Create additional batches if needed
    if (optimizedBatches.length > pendingBatches.length) {
      const startIndex = pendingBatches.length;
      let batchNumber = existingBatches.length + 1;
      
      for (let i = startIndex; i < optimizedBatches.length; i++) {
        const newBatch = {
          id: `batch_${batchNumber}`,
          projectId,
          name: `Batch ${batchNumber}`,
          files: optimizedBatches[i],
          status: 'pending',
          totalLoc: this.calculateBatchSize(optimizedBatches[i], files),
          priority: batchNumber
        };
        
        await this.storage.saveBatch(newBatch);
        batchNumber++;
      }
    }
    
    return this.storage.getBatchesByProject(projectId);
  }
  
  /**
   * Calculate total LOC for a batch
   * @param {Array} fileIds - File IDs in batch
   * @param {Array} allFiles - All project files
   * @returns {number} Total LOC
   */
  calculateBatchSize(fileIds, allFiles) {
    return fileIds.reduce((sum, fileId) => {
      const file = allFiles.find(f => f.id === fileId);
      return sum + (file ? file.estimatedLoc : 0);
    }, 0);
  }
  
  /**
   * Analyze file dependencies based on imports/exports
   * @param {Array} files - Project files
   * @returns {Promise<Object>} File dependency map
   */
  async analyzeFileDependencies(files) {
    const dependencies = {};
    
    // Initialize dependency map
    for (const file of files) {
      dependencies[file.id] = [];
    }
    
    // Build dependency map based on imports
    for (const file of files) {
      if (!file.imports || file.imports.length === 0) continue;
      
      for (const importPath of file.imports) {
        // Find file that exports this import
        const target = files.find(f => {
          // Simple path resolution - should be enhanced
          const relativePath = this.resolveRelativePath(file.path, importPath);
          return f.path === relativePath;
        });
        
        if (target) {
          dependencies[file.id].push(target.id);
        }
      }
    }
    
    return dependencies;
  }
  
  /**
   * Resolve relative import path
   * @param {string} sourcePath - Source file path
   * @param {string} importPath - Import path
   * @returns {string} Resolved path
   */
  resolveRelativePath(sourcePath, importPath) {
    // Basic path resolution - should be enhanced
    if (importPath.startsWith('.')) {
      const sourceDir = sourcePath.substring(0, sourcePath.lastIndexOf('/'));
      return this.normalizePath(`${sourceDir}/${importPath}`);
    }
    
    // Assume node_modules import or absolute path
    return importPath;
  }
  
  /**
   * Normalize a file path (resolve .. and .)
   * @param {string} path - File path
   * @returns {string} Normalized path
   */
  normalizePath(path) {
    // Normalize path (resolve ../ and ./)
    const parts = path.split('/');
    const result = [];
    
    for (const part of parts) {
      if (part === '.' || part === '') continue;
      if (part === '..') {
        result.pop();
        continue;
      }
      result.push(part);
    }
    
    return result.join('/');
  }
  
  /**
   * Create optimized batches based on dependencies
   * @param {Array} files - Project files
   * @param {Object} dependencies - File dependencies
   * @returns {Array} Optimized batches
   */
  createOptimizedBatches(files, dependencies) {
    // Create batches based on dependency order and size constraints
    const sorted = this.sortFilesByDependencies(files, dependencies);
    const batches = [];
    
    let currentBatch = [];
    let currentSize = 0;
    
    for (const file of sorted) {
      if (currentSize + file.estimatedLoc > this.MAX_BATCH_SIZE || 
          currentBatch.length >= this.MAX_BATCH_FILES) {
        // Start a new batch
        batches.push([...currentBatch]);
        currentBatch = [];
        currentSize = 0;
      }
      
      currentBatch.push(file.id);
      currentSize += file.estimatedLoc;
    }
    
    // Add the last batch if not empty
    if (currentBatch.length > 0) {
      batches.push([...currentBatch]);
    }
    
    return batches;
  }
  
  /**
   * Sort files by dependencies
   * @param {Array} files - Project files
   * @param {Object} dependencies - File dependencies
   * @returns {Array} Sorted files
   */
  sortFilesByDependencies(files, dependencies) {
    // Build a dependency graph
    const graph = {};
    
    for (const file of files) {
      graph[file.id] = dependencies[file.id] || [];
    }
    
    // Perform topological sort
    const sorted = [];
    const visited = {};
    const temp = {};
    
    const visit = (fileId) => {
      // If node is permanently visited, do nothing
      if (visited[fileId]) return;
      
      // If node is temporarily visited, we have a cycle
      if (temp[fileId]) {
        console.warn(`Dependency cycle detected for file: ${fileId}`);
        return;
      }
      
      // Mark node as temporarily visited
      temp[fileId] = true;
      
      // Visit all dependencies
      for (const dep of graph[fileId] || []) {
        visit(dep);
      }
      
      // Mark node as permanently visited
      temp[fileId] = false;
      visited[fileId] = true;
      
      // Add node to sorted list
      sorted.push(files.find(f => f.id === fileId));
    };
    
    // Visit all nodes
    for (const file of files) {
      if (!visited[file.id]) {
        visit(file.id);
      }
    }
    
    return sorted;
  }
  
  /**
   * Get batch details with file information
   * @param {string} batchId - Batch ID
   * @returns {Promise<Object>} Batch with file details
   */
  async getBatchWithFiles(batchId) {
    const batch = await this.storage.getBatch(batchId);
    if (!batch) return null;
    
    const fileDetails = [];
    for (const fileId of batch.files) {
      const file = await this.storage.getFile(fileId);
      if (file) {
        fileDetails.push(file);
      }
    }
    
    return {
      ...batch,
      fileDetails
    };
  }
  
  /**
   * Get batch implementation context
   * @param {string} batchId - Batch ID
   * @returns {Promise<Object>} Implementation context
   */
  async getBatchContext(batchId) {
    const batch = await this.storage.getBatch(batchId);
    if (!batch) return null;
    
    const files = [];
    const dependencies = {};
    
    // Get files in this batch
    for (const fileId of batch.files) {
      const file = await this.storage.getFile(fileId);
      if (file) {
        files.push(file);
        
        // Collect import dependencies
        if (file.imports && file.imports.length > 0) {
          dependencies[file.path] = file.imports;
        }
      }
    }
    
    // Get minimal context from dependent files
    const context = {
      files,
      dependencies,
      interfaces: []
    };
    
    // Add interface information for imported files
    for (const file of files) {
      if (!file.imports || file.imports.length === 0) continue;
      
      for (const importPath of file.imports) {
        const dependentFile = await this.storage.getFileByPath(batch.projectId, importPath);
        
        if (dependentFile && dependentFile.exports && dependentFile.exports.length > 0) {
          context.interfaces.push({
            path: dependentFile.path,
            exports: dependentFile.exports
          });
        }
      }
    }
    
    return context;
  }
}

export default BatchManager;
